import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { AdminAuthProvider } from "@/lib/auth";

// Eagerly load only the home page — everything else is split into separate chunks
import Home from "@/pages/Home";

// All other pages lazy-loaded — browser only downloads them when the user navigates there
const Services      = React.lazy(() => import("@/pages/Services"));
const ServiceDetail = React.lazy(() => import("@/pages/ServiceDetail"));
const CaseStudies   = React.lazy(() => import("@/pages/CaseStudies"));
const CaseStudyDetail = React.lazy(() => import("@/pages/CaseStudyDetail"));
const Industries    = React.lazy(() => import("@/pages/Industries"));
const About         = React.lazy(() => import("@/pages/About"));
const Partner       = React.lazy(() => import("@/pages/Partner"));
const Blog          = React.lazy(() => import("@/pages/Blog"));
const BlogDetail    = React.lazy(() => import("@/pages/BlogDetail"));
const Contact       = React.lazy(() => import("@/pages/Contact"));
const Careers       = React.lazy(() => import("@/pages/Careers"));
const CareerDetail  = React.lazy(() => import("@/pages/CareerDetail"));
const Products      = React.lazy(() => import("@/pages/Products"));
const ProductDetail = React.lazy(() => import("@/pages/ProductDetail"));

// Admin pages — rarely visited, large code, split aggressively
const TalkToUs        = React.lazy(() => import("@/pages/TalkToUs"));
const SocialLinks     = React.lazy(() => import("@/pages/SocialLinks"));
const Events          = React.lazy(() => import("@/pages/Events"));
const EventDetail     = React.lazy(() => import("@/pages/EventDetail"));
const EventGallery    = React.lazy(() => import("@/pages/EventGallery"));
const Sitemap         = React.lazy(() => import("@/pages/Sitemap"));
const NotFound        = React.lazy(() => import("@/pages/NotFound"));
const AdminLogin      = React.lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard  = React.lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminPostEditor = React.lazy(() => import("@/pages/admin/AdminPostEditor"));
const AdminCareers    = React.lazy(() => import("@/pages/admin/AdminCareers"));
const AdminJobEditor  = React.lazy(() => import("@/pages/admin/AdminJobEditor"));
const AdminUsers      = React.lazy(() => import("@/pages/admin/AdminUsers"));
const AdminChatLogs   = React.lazy(() => import("@/pages/admin/AdminChatLogs"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);
  return null;
}

// Minimal fallback — invisible so layout doesn't jump
function PageFallback() {
  return <div className="min-h-screen bg-[#0A1628]" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <ScrollToTop />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/"                   element={<Home />} />
              <Route path="/about"              element={<About />} />
              <Route path="/services"           element={<Services />} />
              <Route path="/services/:slug"     element={<ServiceDetail />} />
              <Route path="/case-studies"       element={<CaseStudies />} />
              <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
              <Route path="/industries"         element={<Industries />} />
              <Route path="/partner"            element={<Partner />} />
              <Route path="/blog"               element={<Blog />} />
              <Route path="/blog/:slug"         element={<BlogDetail />} />
              <Route path="/contact"            element={<Contact />} />
              <Route path="/careers"            element={<Careers />} />
              <Route path="/careers/:slug"      element={<CareerDetail />} />
              <Route path="/products"           element={<Products />} />
              <Route path="/products/:slug"     element={<ProductDetail />} />
              <Route path="/talk-to-us"         element={<TalkToUs />} />
              <Route path="/events"             element={<Events />} />
              <Route path="/events/:slug"       element={<EventDetail />} />
              <Route path="/events/:slug/gallery" element={<EventGallery />} />
              <Route path="/sitemap"            element={<Sitemap />} />
            </Route>
            <Route path="/admin/login"          element={<AdminLogin />} />
            <Route path="/admin"                element={<AdminDashboard />} />
            <Route path="/admin/posts/new"      element={<AdminPostEditor />} />
            <Route path="/admin/posts/:postId"  element={<AdminPostEditor />} />
            <Route path="/admin/careers"        element={<AdminCareers />} />
            <Route path="/admin/careers/new"    element={<AdminJobEditor />} />
            <Route path="/admin/careers/:jobId" element={<AdminJobEditor />} />
            <Route path="/admin/users"          element={<AdminUsers />} />
            <Route path="/admin/chat-logs"      element={<AdminChatLogs />} />
            {/* Standalone brand pages — no nav/footer */}
            <Route path="/links"                element={<SocialLinks />} />
            <Route path="*"                     element={<NotFound />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors closeButton />
      </AdminAuthProvider>
    </BrowserRouter>
  );
}
